import * as React from "react"
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react"
import { useMediaQuery } from "react-responsive";
import { x_LARGE_SCREEN } from "../../../assets/Utils/generic";
import Bowser from 'bowser';

import { cn } from "../../../assets/Utils/generic"
import { HTMLAttributes } from "react"

import IconChevron from "../../../assets/icons/IconChevron";

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: "horizontal" | "vertical"
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />")
  }

  return context
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins
  )
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    api?.scrollNext()
  }, [api])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault()
        scrollPrev()
      } else if (event.key === "ArrowRight") {
        event.preventDefault()
        scrollNext()
      }
    },
    [scrollPrev, scrollNext]
  )

  React.useEffect(() => {
    if (!api || !setApi) return
    setApi(api)
  }, [api, setApi])

  React.useEffect(() => {
    if (!api) return
    onSelect(api)
    api.on("reInit", onSelect)
    api.on("select", onSelect)

    return () => {
      api?.off("select", onSelect)
    }
  }, [api, onSelect])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation:
          orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn("relative group", className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
  const { carouselRef, orientation } = useCarousel()

   const isxLargeScreen = useMediaQuery({ query: x_LARGE_SCREEN });

  return (
    <div
      ref={carouselRef}
      className="overflow-hidden"
      data-slot="carousel-content"
    >
      <div
        className={cn(
          "flex",
          orientation === "horizontal" ? ((!isxLargeScreen && "-ml-4") || "ml-0") : "-mt-4 flex-col",
          className
        )}
        {...props}
      />
    </div>
  )
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation } = useCarousel()

  const isxLargeScreen = useMediaQuery({ query: x_LARGE_SCREEN });

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? ((!isxLargeScreen && "pl-4") || "pl-0") : "pt-4",
        className
      )}
      {...props}
    />
  )
}

function CarouselPrevious({
  className,
  ...props
}: HTMLAttributes<HTMLButtonElement>) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()

  const bowserParser = Bowser.getParser(navigator.userAgent);
  const isMobile = bowserParser.getPlatformType() === "mobile"

  return (
    <>
      {(canScrollPrev && !isMobile) && <button
        type="button"
        data-slot="carousel-previous"
        className={cn(
          "absolute size-7 rounded-full items-center justify-center bg-white shadow-[0px_0px_20px_5px_rgba(0,_0,_0,_0.25)] border-none touch-hitbox hidden group-hover:flex",
          orientation === "horizontal"
            ? "top-1/2 left-2 -translate-y-1/2"
            : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
          className
        )}
        disabled={!canScrollPrev}
        onClick={(event) => {
          event.stopPropagation()
          scrollPrev()
        }}
        {...props}
      >
        <IconChevron className="size-3 text-[#747474]"/>
        <span className="sr-only">Previous slide</span>
      </button>}
    </>
  )
}

function CarouselNext({
  className,
  ...props
}: HTMLAttributes<HTMLButtonElement>) {
  const { orientation, scrollNext, canScrollNext } = useCarousel()

  const bowserParser = Bowser.getParser(navigator.userAgent);
  const isMobile = bowserParser.getPlatformType() === "mobile"

  return (
    <>
      {(canScrollNext && !isMobile) && <button
        type="button"
        data-slot="carousel-next"
        className={cn(
          "absolute size-7 rounded-full items-center justify-center bg-white shadow-[0px_0px_20px_5px_rgba(0,_0,_0,_0.25)] border-none touch-hitbox hidden group-hover:flex",
          orientation === "horizontal"
            ? "top-1/2 right-2 -translate-y-1/2"
            : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
          className
        )}
        disabled={!canScrollNext}
        onClick={(event) => {
          event.stopPropagation()
          scrollNext()
        }}
        {...props}
      >
        <IconChevron className="size-3 text-[#747474] rotate-180"/>
        <span className="sr-only">Next slide</span>
      </button>}
    </>
  )
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
}
